import type { SortingState } from '@tanstack/react-table';
import { SkipNav, Spinner } from '@cmsgov/design-system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { useContractsSearchQuery } from '@access-cms/api-client';

import Table, { TableControls, TableFooter } from '../../components/Table';
import TableResultSummary from '../../components/TableResultSummary';
import { useAlerts, useDebounce } from '../../hooks';
import { getTableMinHeight, getTableMinWidth } from '../../utils';
import { useContractsColumns } from './hooks';
import { CONTRACT_STATUS_FILTER_OPTIONS, type ContractStatus } from './utils/contractStatus';

import './Contract.css';
import { ROUTES } from '@/constants';

export interface ContractListItem {
  id?: string;
  isDelegatedToCurrentUser?: boolean;
  contractNumber?: string;
  contractName?: string;
  companyName?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractStatus?: string;
  primaryCorUserId?: string;
  primaryCorFirstName?: string | null;
  primaryCorLastName?: string | null;
}

const DEFAULT_RECORDS_PER_PAGE = 10;
const CONTRACTS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100].map((value) => ({
  label: `${value} contracts per page`,
  value: value.toString(),
}));
const ALLOWED_STATUSES = CONTRACT_STATUS_FILTER_OPTIONS.map((option) => option.value);
const CONTRACT_SORT_FIELDS = {
  companyName: 'company_name',
  contractEndDate: 'contract_end_date',
  contractNumber: 'contract_number',
  contractStartDate: 'contract_start_date',
  corName: 'primary_cor_name',
} as const;

type ContractSortField = keyof typeof CONTRACT_SORT_FIELDS;
type ContractSortOrder = 'asc' | 'desc';

function getContractSortField(sort: string | null): ContractSortField | undefined {
  return Object.entries(CONTRACT_SORT_FIELDS).find(([, value]) => value === sort)?.[0] as ContractSortField | undefined;
}

function ManageContracts() {
  const navigate = useNavigate();
  const { dismissAlert, errorAlert } = useAlerts();
  const [searchParams, setSearchParams] = useSearchParams();
  const baseTestId = 'manage-contracts-page';

  const searchQuery = searchParams.get('keyword') ?? '';
  const sortField = getContractSortField(searchParams.get('sort'));
  const sortOrder: ContractSortOrder = searchParams.get('order') === 'asc' ? 'asc' : 'desc';
  const sortBy: SortingState = sortField ? [{ desc: sortOrder === 'desc', id: sortField }] : [];
  const statuses = searchParams
    .getAll('status')
    .filter((value): value is ContractStatus => ALLOWED_STATUSES.includes(value));

  const pageParam = Number(searchParams.get('page'));
  const limitParam = Number(searchParams.get('limit'));
  const currentPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const recordsPerPage = CONTRACTS_PER_PAGE_OPTIONS.some((option) => option.value === String(limitParam))
    ? limitParam
    : DEFAULT_RECORDS_PER_PAGE;

  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedKeyword = useDebounce(searchInput, 300);

  const updateSearchParams = useCallback(
    (updates: {
      keyword?: string;
      statuses?: string[];
      page?: number;
      limit?: number;
      sort?: ContractSortField | null;
      order?: ContractSortOrder | null;
    }) => {
      setSearchParams(
        (previousParams) => {
          const nextParams = new URLSearchParams(previousParams);

          if (updates.keyword !== undefined) {
            if (updates.keyword) nextParams.set('keyword', updates.keyword);
            else nextParams.delete('keyword');
          }

          if (updates.statuses !== undefined) {
            nextParams.delete('status');
            updates.statuses.forEach((value) => nextParams.append('status', value));
          }

          if (updates.page !== undefined) {
            if (updates.page === 1) nextParams.delete('page');
            else nextParams.set('page', String(updates.page));
          }

          if (updates.limit !== undefined) {
            if (updates.limit === DEFAULT_RECORDS_PER_PAGE) nextParams.delete('limit');
            else nextParams.set('limit', String(updates.limit));
          }

          if (updates.sort !== undefined) {
            if (updates.sort) nextParams.set('sort', CONTRACT_SORT_FIELDS[updates.sort]);
            else nextParams.delete('sort');
          }

          if (updates.order !== undefined) {
            if (updates.order) nextParams.set('order', updates.order);
            else nextParams.delete('order');
          }

          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const contractsQuery = useContractsSearchQuery({
    ...(debouncedKeyword ? { keyword: debouncedKeyword } : {}),
    ...(statuses.length > 0 ? { contractStatus: statuses } : {}),
    ...(sortField ? { order: sortOrder, sort: CONTRACT_SORT_FIELDS[sortField] } : {}),
    limit: recordsPerPage,
    page: currentPage,
  });
  const loadErrorAlertIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (contractsQuery.isError && !loadErrorAlertIdRef.current) {
      loadErrorAlertIdRef.current = errorAlert({
        message: "Sorry, we couldn't load your contracts. Please try again.",
        title: 'Unable to load contracts',
      });
    }

    return () => {
      if (loadErrorAlertIdRef.current) {
        dismissAlert(loadErrorAlertIdRef.current);
        loadErrorAlertIdRef.current = null;
      }
    };
  }, [contractsQuery.isError, dismissAlert, errorAlert]);

  const serverMeta = contractsQuery.data?.meta?.pagination;
  const totalRecords = serverMeta?.total ?? 0;
  const totalPages = serverMeta?.totalPages ?? 0;

  const visibleData = (contractsQuery.data?.data ?? []) as ContractListItem[];
  const handleViewDetails = useCallback(
    (contractId: string) => {
      navigate(generatePath(ROUTES.CONTRACT_DETAILS, { id: contractId }));
    },
    [navigate],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      updateSearchParams({ keyword: value, page: 1 });
    },
    [updateSearchParams],
  );

  const handleFiltersApply = useCallback(
    (filters: string[]) => {
      updateSearchParams({ page: 1, statuses: filters });
    },
    [updateSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      updateSearchParams({ page });
    },
    [updateSearchParams],
  );

  const handleRecordsPerPageChange = useCallback(
    (value: number) => {
      updateSearchParams({ limit: value, page: 1 });
    },
    [updateSearchParams],
  );

  const handleSortingChange = useCallback(
    (nextSorting: SortingState) => {
      const nextSort = nextSorting[0];

      if (!nextSort || !Object.hasOwn(CONTRACT_SORT_FIELDS, nextSort.id)) {
        updateSearchParams({ order: null, page: 1, sort: null });

        return;
      }

      updateSearchParams({
        order: nextSort.desc ? 'desc' : 'asc',
        page: 1,
        sort: nextSort.id as ContractSortField,
      });
    },
    [updateSearchParams],
  );

  const columns = useContractsColumns({ onViewDetails: handleViewDetails });

  const isEmpty = visibleData.length === 0;
  const isSearchSettled = searchInput === debouncedKeyword;
  const isResultSettled = isSearchSettled && !contractsQuery.isFetching && !contractsQuery.isError;
  const tableMinHeight = getTableMinHeight(recordsPerPage);
  const tableMinWidth = getTableMinWidth(columns);

  return (
    <>
      <SkipNav href='#content'>Skip to page content</SkipNav>
      <div
        className='ds-l-row'
        data-testid={baseTestId}>
        <article
          aria-labelledby='page-heading'
          className='ds-content ds-l-col--12'
          data-testid={`${baseTestId}-content`}
          id='content'>
          <h1
            data-testid={`${baseTestId}-heading`}
            id='page-heading'>
            Manage Contracts
          </h1>
          <div
            aria-hidden='true'
            className='manage-contracts__divider'
            data-testid={`${baseTestId}-divider`}
          />

          <div
            className='manage-contracts__body contract-roster-tab-content'
            data-testid={`${baseTestId}-body`}>
            <div>
              <p
                className='manage-contracts__intro'
                data-testid={`${baseTestId}-intro-primary`}>
                View the list of contracts for which you are the COR, Roster Owner, or Delegate Roster Owner.
              </p>
              <p
                className='manage-contracts__intro'
                data-testid={`${baseTestId}-intro-secondary`}>
                Click the View Details link for any contract to view more information about the Contract or its Roster.
              </p>
            </div>

            <div
              className='access-table-shell manage-contracts__table-container'
              data-testid={`${baseTestId}-table-container`}>
              <div
                className='manage-contracts__table-toolbar'
                data-testid={`${baseTestId}-controls`}>
                <TableControls
                  className='manage-contracts__controls'
                  data-testid={`${baseTestId}-table-controls`}
                  filterOptions={CONTRACT_STATUS_FILTER_OPTIONS}
                  resultCount={isResultSettled ? totalRecords : undefined}
                  searchInput={searchInput}
                  searchPlaceholder='Search...'
                  selectedFilters={statuses}
                  onFiltersApply={handleFiltersApply}
                  onSearchChange={handleSearchChange}
                />
              </div>

              <div
                className='access-table-wrapper'
                data-testid={`${baseTestId}-table`}>
                <div style={{ minHeight: `${tableMinHeight}px`, minWidth: `${tableMinWidth}px` }}>
                  {contractsQuery.isLoading ? (
                    <div
                      className='access-table-status'
                      data-testid={`${baseTestId}-loading`}>
                      <div className='access-table-status__card'>
                        <Spinner aria-valuetext='Loading contracts...' />
                      </div>
                    </div>
                  ) : isEmpty ? (
                    <div
                      className='access-table-status'
                      data-testid={`${baseTestId}-empty`}>
                      <div className='access-table-status__card'>
                        <p
                          className='access-table-status__message'
                          data-testid={`${baseTestId}-empty-message`}>
                          {contractsQuery.isError
                            ? 'Contracts couldn\u2019t be loaded.'
                            : debouncedKeyword || statuses.length > 0
                            ? 'No contracts match your search or filter criteria.'
                            : 'No contracts available.'}
                        </p>
                        {!contractsQuery.isError && (debouncedKeyword || statuses.length > 0) && (
                          <p
                            className='access-table-status__hint'
                            data-testid={`${baseTestId}-empty-hint`}>
                            Try adjusting your search terms or filters.
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Table
                      enableSorting
                      manualSorting
                      ariaLabel='Manage contracts table'
                      caption={`Manage contracts table showing ${totalRecords} entries`}
                      className='manage-contracts__table'
                      columns={columns}
                      data={visibleData}
                      data-testid={`${baseTestId}-table-component`}
                      sortBy={sortBy}
                      onSortingChange={handleSortingChange}
                    />
                  )}
                </div>
              </div>

              {!contractsQuery.isLoading && !isEmpty && (
                <TableFooter
                  className='manage-contracts__table-footer'
                  currentPage={currentPage}
                  data-testid={`${baseTestId}-footer`}
                  recordsDropdownName='manage-contracts-footer-records-per-page'
                  recordsPerPage={recordsPerPage}
                  recordsPerPageOptions={CONTRACTS_PER_PAGE_OPTIONS}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onRecordsPerPageChange={handleRecordsPerPageChange}
                />
              )}
            </div>
            {!contractsQuery.isLoading && !isEmpty && (
              <TableResultSummary
                data-testid={`${baseTestId}-summary`}
                pagination={serverMeta}
                rowCount={visibleData.length}
              />
            )}
          </div>
        </article>
      </div>
    </>
  );
}

export default ManageContracts;
